/**
 * Rechte Spalte der Werkstatt für Kommentare und Änderungsvorschläge des gewählten Abschnitts.
 *
 * Kommentare sind Fäden, verankert über eine Marke im Text; jeder Mitglied des Rechteinhabers kann
 * antworten, erledigen dürfen Autor, Leitung oder wer den Faden begonnen hat. Vorschläge kommen aus
 * dem Plate-Inhalt — annehmen oder ablehnen geht im Editor (Autor, Leitung).
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, RotateCcw, X } from "lucide-react";

import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { cn } from "../lib/utils";
import type { Discussion } from "./GuidelinesAPI";
import type { SuggestionSummary } from "./plate/plateValue";

const when = (at: string | number) => new Date(at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });

export function CommentsPanel(props: {
  discussions: Discussion[];
  /** Ein neuer Faden, dessen Marke schon im Text steht — wartet auf seinen ersten Kommentar. */
  pending: { discussionId: string; quote: string } | null;
  selected: string | null;
  canResolveAll: boolean;
  currentUser: string;
  onCreate: (body: string) => void;
  onCancelPending: () => void;
  onReply: (discussionId: string, body: string) => void;
  onResolve: (discussionId: string, resolved: boolean) => void;
  onSelect: (discussionId: string) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const [replies, setReplies] = useState<Record<string, string>>({});
  const [showResolved, setShowResolved] = useState(false);
  const open = props.discussions.filter((d) => !d.resolved);
  const done = props.discussions.filter((d) => d.resolved);

  return (
    <div className="flex flex-col gap-3">
      {props.pending ? (
        <div className="rounded-md border border-primary p-3 text-sm">
          <blockquote className="mb-2 border-l-2 pl-2 text-xs italic text-muted-foreground">„{props.pending.quote}“</blockquote>
          <Textarea autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t("guidelinesPage.comments.placeholder")} className="min-h-[4rem] text-sm" />
          <div className="mt-2 flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={props.onCancelPending}>{t("guidelinesPage.cancel")}</Button>
            <Button size="sm" disabled={!draft.trim()} onClick={() => { props.onCreate(draft.trim()); setDraft(""); }}>{t("guidelinesPage.comments.post")}</Button>
          </div>
        </div>
      ) : null}
      {!open.length && !props.pending ? <p className="text-sm text-muted-foreground">{t("guidelinesPage.comments.none")}</p> : null}
      {[...open, ...(showResolved ? done : [])].map((d) => (
        <div
          key={d.discussionId}
          data-discussion-id={d.discussionId}
          onClick={() => props.onSelect(d.discussionId)}
          className={cn("rounded-md border p-3 text-sm", d.discussionId === props.selected && "ring-2 ring-primary", d.resolved && "opacity-60")}
        >
          {d.quote ? <blockquote className="mb-2 border-l-2 pl-2 text-xs italic text-muted-foreground">„{d.quote}“</blockquote> : null}
          {d.comments.map((c) => (
            <div key={c.id} className="mb-2">
              <div className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{c.name}</span> · {when(c.at)}
              </div>
              <div className="whitespace-pre-wrap">{c.body}</div>
            </div>
          ))}
          {!d.resolved ? (
            <div className="flex items-end gap-2" onClick={(e) => e.stopPropagation()}>
              <Textarea
                value={replies[d.discussionId] ?? ""}
                onChange={(e) => setReplies((r) => ({ ...r, [d.discussionId]: e.target.value }))}
                placeholder={t("guidelinesPage.comments.reply")}
                className="min-h-[2.25rem] flex-1 text-sm"
              />
              <Button
                size="sm"
                variant="outline"
                disabled={!(replies[d.discussionId] ?? "").trim()}
                onClick={() => { props.onReply(d.discussionId, replies[d.discussionId].trim()); setReplies((r) => ({ ...r, [d.discussionId]: "" })); }}
              >
                {t("guidelinesPage.comments.send")}
              </Button>
            </div>
          ) : null}
          {props.canResolveAll || d.comments[0]?.by === props.currentUser ? (
            <div className="mt-2 flex justify-end" onClick={(e) => e.stopPropagation()}>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => props.onResolve(d.discussionId, !d.resolved)}>
                {d.resolved ? <RotateCcw size={14} className="mr-1" /> : <Check size={14} className="mr-1" />}
                {d.resolved ? t("guidelinesPage.comments.reopen") : t("guidelinesPage.comments.resolve")}
              </Button>
            </div>
          ) : null}
        </div>
      ))}
      {done.length ? (
        <button type="button" className="text-left text-xs text-muted-foreground underline" onClick={() => setShowResolved((v) => !v)}>
          {showResolved ? t("guidelinesPage.comments.hideResolved") : t("guidelinesPage.comments.showResolved", { count: done.length })}
        </button>
      ) : null}
    </div>
  );
}

export function SuggestionsPanel(props: {
  suggestions: SuggestionSummary[];
  /** Nur im Editor lässt sich annehmen oder ablehnen. */
  editing: boolean;
  canDecide: boolean;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
}) {
  const { t } = useTranslation();
  if (!props.suggestions.length) return <p className="text-sm text-muted-foreground">{t("guidelinesPage.suggestions.none")}</p>;
  return (
    <div className="flex flex-col gap-2">
      {!props.editing && props.canDecide ? <p className="text-xs text-muted-foreground">{t("guidelinesPage.suggestions.editToDecide")}</p> : null}
      {props.suggestions.map((s) => (
        <div key={s.id} className="rounded-md border p-3 text-sm">
          <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
            <span className={cn("rounded px-1.5", s.type === "insert" ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100" : "bg-rose-100 text-rose-900 dark:bg-rose-900/40 dark:text-rose-100")}>
              {t(`guidelinesPage.suggestions.${s.type}`)}
            </span>
            <span>{s.userId}</span>
            <span>· {when(s.createdAt)}</span>
          </div>
          <div className={cn("whitespace-pre-wrap", s.type === "remove" && "text-muted-foreground line-through")}>{s.text || "¶"}</div>
          {props.editing && props.canDecide ? (
            <div className="mt-2 flex justify-end gap-2">
              <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => props.onAccept(s.id)}>
                <Check size={14} className="mr-1" />
                {t("guidelinesPage.suggestions.accept")}
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => props.onReject(s.id)}>
                <X size={14} className="mr-1" />
                {t("guidelinesPage.suggestions.reject")}
              </Button>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
